import { Box, lighten, Stack, Typography, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import dayjs from 'dayjs';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  LabelList
} from 'recharts';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import * as urls from 'routes/config';
import DateRangePicker from 'components/Common/DateRangePicker';
import { MdAccessAlarm, MdOutlineTaskAlt } from 'react-icons/md';
import TotalAnsweredIcon from '@/assets/icons/TotalAnsweredIcon';
import AssistanceWorkflowsIcon from '@/assets/icons/AssistanceWorkflowsIcon';
import OpportunityWorkflowsIcon from '@/assets/icons/OpportunityWorkflowsIcon';
import TransferredIcon from '@/assets/icons/TransferredIcon';
import { useGetPerformance } from '@/apis/business/agent/performance/hook';
import { formatDuration, titleCase } from '@/utils/helperFunctions';
import PerformanceLoader from './components/PerformanceLoader';
import WorkflowBreakdownYAxisTick from './components/WorkflowBreakdownYAxisTick';
import WorkflowBreakdownTooltip from './components/WorkflowBreakdownTooltip';
import AIRecommendationCentre from './components/AIRecommendationCentre';
import { BuilderEmptyStateView } from '@/components/Common/BuilderEmptyStateView';
import { formatTaskCompletionLabel, resolveTaskCompletionConfig } from './components/TaskCompleted';
import { TaskCompletionCardItem } from '@/apis/business/agent/performance/types';
import OutboundPerformance from './Outbound';

type ActionBreakdownItem = {
  intent: string;
  name: string;
  value: number;
  color: string;
  type?: string;
  workflow_type?: string;
};

type SignalsDeepLinkParams = {
  task_completed?: string;
  workflow?: string;
  start_date?: string;
  end_date?: string;
};

const buildSignalsSearchParams = (params: SignalsDeepLinkParams) => {
  const search = new URLSearchParams();
  if (params.task_completed) search.set('task_completed', params.task_completed);
  if (params.workflow) search.set('workflow', params.workflow);
  if (params.start_date) search.set('start_date', params.start_date);
  if (params.end_date) search.set('end_date', params.end_date);
  return search.toString();
};

export default function Performance() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { locationId, agentId } = useParams<{ locationId: string; agentId: string }>();
  const { control, watch } = useForm();

  const filters = watch();
  const dateArr = filters?.date?.split(',');
  const updatedFilters = {
    start_date: dateArr?.[0] ?? dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
    end_date: dateArr?.[1] ?? dayjs().format('YYYY-MM-DD')
  };

  const { data: performanceData, isLoading } = useGetPerformance({
    ...updatedFilters
  });

  const isOutbound = performanceData?.agent_interaction === 'outbound';

  if (isLoading) {
    return <PerformanceLoader />;
  }

  if (isOutbound) {
    return <OutboundPerformance />;
  }

  const isAllZero = performanceData?.customer_breakdown?.every((item: any) => item.value === 0);

  const metricCards: { title: string; value: number; icon: React.ReactNode; subValue?: React.ReactNode }[] = [
    {
      title: 'Total Answered',
      value: performanceData?.overview.total_answered_calls ?? 0,
      icon: <TotalAnsweredIcon />,
      subValue: (
        <Stack direction='row' alignItems='center' mt={1} gap={0.5} color='text.secondary'>
          <Typography component='span' fontSize={12} color='text.secondary'>
            ·
          </Typography>
          <TransferredIcon />
          <Typography component='span' fontSize={12} color='text.secondary'>
            {performanceData?.overview?.transferred_calls ?? 0} Transferred
          </Typography>
        </Stack>
      )
    },
    {
      title: 'Assistance Workflows',
      value: performanceData?.overview?.assistance_workflow_calls ?? 0,
      icon: <AssistanceWorkflowsIcon />
    },
    {
      title: 'Opportunity Workflows',
      value: performanceData?.overview?.opportunity_workflow_calls ?? 0,
      icon: <OpportunityWorkflowsIcon />
    },
    {
      title: 'Task Completed',
      value: performanceData?.overview?.tasks_completed ?? 0,
      icon: <MdOutlineTaskAlt size={14} />
    }
  ];

  const tasksCompletion = performanceData?.tasks_completion;

  const taskCompletionCard: TaskCompletionCardItem[] = (() => {
    const rows = Array.isArray(tasksCompletion) ? tasksCompletion : [];
    const nameCounts = rows.reduce<Record<string, number>>((acc, row) => {
      const key = row.name || '';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    return rows.map(
      (row: {
        name: string;
        type?: string | null;
        value: number;
        tool_name?: string | null;
        worksheet_name?: string | null;
        executor_label?: string | null;
      }) => {
        const showType = (nameCounts[row.name] || 0) > 1;
        const config = resolveTaskCompletionConfig(row.name, row.type, row.executor_label, row.worksheet_name);

        return {
          workflowKey: row.name,
          label: formatTaskCompletionLabel(row.name, row.type, showType, row.worksheet_name, row.executor_label),
          value: row.value ?? 0,
          type: row.type,
          icon: config.icon
        };
      }
    );
  })();

  const navigateToSignals = (linkParams: Pick<SignalsDeepLinkParams, 'task_completed' | 'workflow'>) => {
    if (!locationId || !agentId) return;

    const query = buildSignalsSearchParams({
      ...linkParams,
      start_date: updatedFilters.start_date,
      end_date: updatedFilters.end_date
    });

    navigate(`${urls.agentBase(locationId, agentId)}/${urls.home.signals}${query ? `?${query}` : ''}`);
  };

  const handleTaskCardClick = (workflowKey: string) => {
    navigateToSignals({ task_completed: workflowKey });
  };

  const handleIntentBarClick = (payload: ActionBreakdownItem) => {
    if (!payload?.intent) return;
    navigateToSignals({ workflow: payload.intent });
  };

  return (
    <>
      <AIRecommendationCentre />
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        mb={3}
        flexDirection={{ xs: 'column', lg: 'row' }}
        gap={2}
      >
        <Typography variant='h6' fontWeight={500} sx={{ fontSize: { xs: '1.125rem', lg: '1.25rem' } }}>
          Performance
        </Typography>

        <Box width={{ xs: '100%', lg: 260 }}>
          <DateRangePicker
            name='date'
            control={control}
            defaultValue={`${dayjs().subtract(30, 'day').format('YYYY-MM-DD')},${dayjs().format('YYYY-MM-DD')}`}
          />
        </Box>
      </Box>

      <Box
        bgcolor='#fff'
        border='1px solid #ECECEC'
        borderLeft={`4px solid ${theme.palette.primary.main}`}
        borderRadius={2}
        padding='16px'
        mb={3}
      >
        <Grid container spacing={2} alignItems='center'>
          <Grid size={{ xs: 12, lg: 6 }} display='flex' direction='row' gap={2.5}>
            <Box padding='20px' bgcolor={theme.palette.primary.light} borderRadius='12px'>
              <MdAccessAlarm size={26} color={theme.palette.primary.main} />
            </Box>
            <Box>
              <Typography fontSize={14} color='text.secondary' fontWeight={400}>
                Total Staff Time Saved
              </Typography>
              <Typography fontSize={24} fontWeight={500} color={theme.palette.primary.main}>
                {formatDuration(performanceData?.overview?.total_call_duration)}
              </Typography>
              <Typography color='text.secondary' fontSize={12}>
                Equivalent to{' '}
                <Box component='span' color='primary.main'>
                  ~{performanceData?.overview?.working_days} working
                </Box>{' '}
                days this period
              </Typography>
            </Box>
          </Grid>

          <Grid size={{ xs: 6, lg: 3 }} borderLeft='1px solid #ECECEC' paddingLeft={3}>
            <Typography fontSize={14} color='text.secondary'>
              Avg Call Duration
            </Typography>
            <Typography fontWeight={500} fontSize={20} color={theme.palette.primary.main}>
              {formatDuration(performanceData?.overview?.average_duration)}
            </Typography>
          </Grid>

          <Grid size={{ xs: 6, lg: 3 }} borderLeft='1px solid #ECECEC' paddingLeft={3}>
            <Typography fontSize={14} color='text.secondary'>
              After-Hours Answered
            </Typography>
            <Typography fontWeight={500} fontSize={20} color={theme.palette.primary.main}>
              {performanceData?.overview?.after_hours_calls}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={2} mb={3}>
        {metricCards.map((card, idx) => (
          <Grid key={idx} size={{ xs: 6, lg: 3 }}>
            <Box
              bgcolor='#fff'
              border='1px solid #ECECEC'
              borderRadius={2}
              p='16px'
              borderLeft={`4px solid ${theme.palette.primary.main}`}
            >
              <Stack direction='row' justifyContent='space-between'>
                <Typography fontWeight={500} color='text.secondary' fontSize={14}>
                  {card.title}
                </Typography>
                <Box
                  paddingX='10px'
                  paddingY='4px'
                  bgcolor={theme.palette.primary.light}
                  color={theme.palette.primary.main}
                  borderRadius='50%'
                >
                  {card.icon}
                </Box>
              </Stack>
              <Stack direction='row' alignItems='center' gap={1}>
                <Typography fontSize={20} fontWeight={500} color='#09090A'>
                  {card.value}
                </Typography>
                {card.subValue}
              </Stack>
            </Box>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Box bgcolor='#fff' border='1px solid #ECECEC' borderRadius={2} p={3}>
            <Typography fontSize={20} fontWeight={500}>
              Call Workflow Breakdown
            </Typography>
            <Typography mb={2} fontSize={14} color='text.secondary'>
              Distribution of calls by intent. One call may have multiple intents.
            </Typography>

            {performanceData?.action_breakdown?.length ? (
              <ResponsiveContainer width='100%' height={420}>
                <BarChart
                  data={performanceData?.action_breakdown ?? []}
                  layout='vertical'
                  margin={{ left: 25, right: 35 }}
                  onClick={(chartData) => {
                    const payload = chartData?.activePayload?.[0]?.payload as ActionBreakdownItem | undefined;
                    if (payload) handleIntentBarClick(payload);
                  }}
                >
                  <XAxis type='number' />
                  <YAxis
                    type='category'
                    dataKey='name'
                    width={180}
                    interval={0}
                    // axisLine={false}
                    tickLine={false}
                    tick={(props) => (
                      <WorkflowBreakdownYAxisTick {...props} data={performanceData?.action_breakdown ?? []} />
                    )}
                  />
                  <Tooltip
                    content={<WorkflowBreakdownTooltip />}
                    cursor={{ fill: 'rgba(0, 0, 0, 0.04)' }}
                    offset={0}
                    allowEscapeViewBox={{ x: true, y: true }}
                    wrapperStyle={{ outline: 'none', zIndex: 10 }}
                  />
                  <Bar dataKey='value' radius={[0, 8, 8, 0]} barSize={28} cursor='pointer'>
                    {performanceData?.action_breakdown?.map((entry: ActionBreakdownItem, index: number) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                    <LabelList
                      dataKey='value'
                      position='right'
                      style={{
                        fill: '#6b7280',
                        maxWidth: '5px',
                        fontSize: 13,
                        fontWeight: 500
                      }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box width='100%' my={14.75}>
                <BuilderEmptyStateView />
              </Box>
            )}
          </Box>
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <Box bgcolor='#fff' border='1px solid #ECECEC' borderRadius={2} p={3}>
            <Typography fontSize={20} fontWeight={500}>
              Caller Breakdown
            </Typography>
            <Typography mb={2} fontSize={14} color='text.secondary'>
              Breakdown of new and repeat callers, based on the selected date range.
            </Typography>

            {isAllZero ? (
              <Box width='100%' my={14.75}>
                <BuilderEmptyStateView />
              </Box>
            ) : (
              <ResponsiveContainer width='100%' height={420}>
                <PieChart>
                  <Pie
                    data={performanceData?.customer_breakdown}
                    dataKey='value'
                    innerRadius={90}
                    outerRadius={140}
                    paddingAngle={2}
                  >
                    <Cell fill={theme.palette.primary.main} />
                    <Cell color='black' fill={lighten(theme.palette.primary.main, 0.4)} />
                  </Pie>
                  <Legend
                    verticalAlign='bottom'
                    iconType='circle'
                    formatter={(value) => (
                      <span style={{ color: '#000', fontWeight: 400, fontSize: 14, paddingLeft: 5 }}>
                        {titleCase(value)}
                      </span>
                    )}
                  />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Grid>
      </Grid>

      <Box bgcolor='#fff' border='1px solid #ECECEC' borderRadius={2} p={3} mb={3} my={3}>
        <Typography fontSize={20} fontWeight={500}>
          Tasks Completed
        </Typography>
        <Typography fontSize={14} color='text.secondary' mb={3}>
          Actions autonomously completed by your AI agent
        </Typography>

        <Grid container spacing={2}>
          {taskCompletionCard.map((item: TaskCompletionCardItem) => (
            <Grid key={`${item.workflowKey}-${item.type || 'default'}`} size={{ xs: 6, sm: 4, md: 2 }}>
              <Box
                component='button'
                type='button'
                onClick={() => handleTaskCardClick(item.workflowKey)}
                border='1px solid #ECECEC'
                borderRadius='12px'
                p={2}
                textAlign='center'
                height='100%'
                width='100%'
                bgcolor='#fff'
                sx={{
                  cursor: 'pointer',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                  }
                }}
              >
                <Box
                  width={36}
                  height={36}
                  mx='auto'
                  mb={1}
                  borderRadius='8px'
                  display='flex'
                  alignItems='center'
                  justifyContent='center'
                >
                  {item.icon}
                </Box>
                <Typography fontSize={20} fontWeight={600} color='#09090A'>
                  {item.value}
                </Typography>
                <Typography fontSize={12} color='text.secondary' lineHeight={'1.1rem'} marginTop={'0.2rem'}>
                  {item.label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
      <Box mt={3} bgcolor='#fff' border='1px solid #ECECEC' borderRadius={2} p={3}>
        <Typography fontSize={20} fontWeight={500}>
          Inbound Volume by Hour
        </Typography>
        <Typography mb={2} fontSize={14} color='text.secondary'>
          Call volume distribution across all hours of the day
        </Typography>

        <ResponsiveContainer width='100%' height={320}>
          <BarChart data={performanceData?.daily_performance} margin={{ left: -30, top: 10 }}>
            <CartesianGrid strokeDasharray='3 3' vertical={false} />
            <XAxis dataKey='time' />
            <YAxis />
            <Tooltip />
            <Bar dataKey='value' barSize={48} fill={theme.palette.primary.main} radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </>
  );
}
